import { compareHuge, huge, maxHuge, type HugeNumber } from "@/shared/numbers";
import type { HoverIdleSnapshot } from "@/features/hoverIdle/types";
import {
  HOV_GRID_INCREASE_1_UNLOCK_CURRENT_LOOP_HOV_THRESHOLD,
  HOV_GRID_INCREASE_2_UNLOCK_CURRENT_LOOP_HOV_THRESHOLD,
} from "@/features/hoverIdle/unlocks/hoverIdleEffectUnlocks";
import { UPGRADE_MILESTONE_INTERVAL } from "@/features/hoverIdle/core/upgradeMilestones";
import { computeMpmAssignedMpOccupancy } from "@/features/hoverIdle/mpm/mpmCapacity";
import { resolveNHovBranchEffects } from "@/features/hoverIdle/nHov/nHovBranchEffects";
import { resolveTotalHovCoreSpent } from "@/features/hoverIdle/core/cumulativeStatsResolvers";
import { EXPLORATION_TIM_ACCELERATION_UNLOCK_CUMULATIVE_MS } from "@/features/hoverIdle/exploration/explorationDefinitions";
import type { NormalAchievementDefinition } from "./types";

export const HOV_GAIN_FAMILY_UPGRADE_PURCHASE_THRESHOLD = 100;
export const HOV_GAIN_FAMILY_UPGRADE_MARATHON_PURCHASE_THRESHOLD = 10_000;
export const GLOBAL_HOV_GAIN_UPGRADE_MILESTONE_ACHIEVEMENT_LEVEL =
  UPGRADE_MILESTONE_INTERVAL;
export const GLOBAL_HOV_GAIN_UPGRADE_TENTH_MILESTONE_ACHIEVEMENT_LEVEL =
  UPGRADE_MILESTONE_INTERVAL * 10;
export const CHEAP_FOURTH_HOV_GAIN_UNLOCK_COST_THRESHOLD = 1;
export const CHEAP_GLOBAL_HOV_GAIN_MILESTONE_TRIGGER_COST_THRESHOLD = 1;
export const ONE_COST_BATCH_UPGRADE_HOV_GAIN_LEVEL = 1000;
export const ONE_COST_BATCH_UPGRADE_TRIGGER_COST_THRESHOLD = 1;
export const H9G_UPGRADE_MILESTONE_ACHIEVEMENT_LEVEL =
  UPGRADE_MILESTONE_INTERVAL;
export const MASS_HOV_GAIN_FAMILY_UPGRADE_PURCHASE_THRESHOLD = 1000;
export const AMPLIFIER_UPGRADE_PURCHASE_THRESHOLD = 100;
// Counts every MP held under MPM regardless of section toggle state so a
// section toggle cannot lock or unlock these achievements.
export const MUSIC_POINTER_ASSIGNED_MP_THRESHOLD = 3;
export const MOVIE_POINTER_ASSIGNED_MP_THRESHOLD = 4;
export const MASCHINEN_POINTER_ASSIGNED_MP_THRESHOLD = 5;
export const TIM_HOV_ACTIVATION_UNLOCK_HOV_REFINED_FROM_TIM_THRESHOLD = 100_000;
export const TIME_HAS_COME_TOTAL_TIM_SPENT_THRESHOLD = 24;
export const BURN_THE_CORE_TOTAL_HOV_CORE_SPENT_THRESHOLD = 100;
export const FIRST_HOV_TARGET_CELL_INDEX = 0;
export const FOURTH_HOV_TARGET_CELL_INDEX = 3;
const FIRST_OPTIONAL_HOV_TARGET_CELL_INDEX = 1;
export const NINTH_HOV_TARGET_CELL_INDEX = 8;

// Projection window for converting HG/s into a comparable Hov amount for
// the grid-increase thresholds.
export const POP_GRID_INCREASE_TARGET_SECONDS = 1;
export const BOMB_GRID_INCREASE_TARGET_SECONDS = 1;

export const BILLION_HOV_THRESHOLD = 1e9;
export const THOUSAND_AP_THRESHOLD = 1000;
export const EXPLORATION_BIOME_LEVEL_ACHIEVEMENT_THRESHOLD = 2;

// String-encoded so break_eternity.js parses without precision loss.
export const VIGINTILLION_HOV_THRESHOLD = "1e63";
export const GOOGLE_HOV_THRESHOLD = "1e100";

export const NEW_NEW_HOV_EXECUTED_BRANCH_KINDS_THRESHOLD = 2;

export const MANY_NEW_HOV_NHOV_COUNT_THRESHOLD = 10;

export const NHOV_KNOWS_NHOV_HG_EFFECTS_TO_HNG_THRESHOLD = 1e4;

// Unlock fires when an NHov execution leaves the hgDiscount HG cost divisor
// strictly above this threshold.
export const NOT_FULLY_FREE_HG_COST_DIVISOR_THRESHOLD = 1e100;

// Unlock fires when a single MHov execution grows the MHov multiplier by at
// least this factor (multiplierAfter / multiplierBefore). Judged from the
// before/after ratio of the multiplier, not the absolute post-execution value,
// so this stays independent of `pop` / `bomb` Hov/HG-scale thresholds.
export const MASSIVE_MERGE_HOV_MHOV_MULTIPLIER_THRESHOLD = 1e100;

// Unlock fires when the current H9G effective Hov gain per second reaches at
// least this factor times the current H1G effective Hov gain per second.
// Judged from the same per-cell effective values shown in the HG / HnG upgrade
// tooltips, not from branch level, so the condition reflects the displayed
// pre-upgrade value players actually see.
export const RANK_GAP_H9G_TO_H1G_RATIO_THRESHOLD = 1e100;

// Unlock fires when the displayed H1G effective Hov gain per second reaches
// 10^threshold. Judged from log10 of the snapshot value so the condition is
// stable past Number.MAX_VALUE, which lets 10^1000-scale targets stay
// expressible without going through a JS-number threshold.
export const HOV_1_GAIN_TRIES_TOO_LOG10_THRESHOLD = 100;
export const HOV_1_GAIN_GETS_SERIOUS_LOG10_THRESHOLD = 1000;

// Use log10 comparison so the test is stable when the absolute multipliers
// approach `Number.MAX_VALUE`, and clamp inputs to >= 1 so a malformed
// pre-execution multiplier cannot falsely satisfy the condition.
export function isMassiveMergeHovRatioMet(inputs: {
  readonly multiplierBefore: HugeNumber;
  readonly multiplierAfter: HugeNumber;
}): boolean {
  const before = maxHuge(inputs.multiplierBefore, 1);
  const after = maxHuge(inputs.multiplierAfter, 1);
  const log10Gain = after.log10().sub(before.log10());
  return (
    compareHuge(
      log10Gain,
      Math.log10(MASSIVE_MERGE_HOV_MHOV_MULTIPLIER_THRESHOLD),
    ) >= 0
  );
}

// Compares log10 of the displayed pre-upgrade H1G effective Hov gain per
// second against the unlock threshold. Reads the same snapshot field the H1G
// tooltip shows so any H1G-specific exponent bonus is naturally reflected.
function isH1gValueLog10AtLeast(
  snapshot: HoverIdleSnapshot,
  log10Threshold: number,
): boolean {
  const h1g =
    snapshot.hovTargetCells[FIRST_HOV_TARGET_CELL_INDEX]
      ?.effectiveHovGainPerSecond;
  if (!h1g) return false;
  if (compareHuge(h1g, 1) < 0) return false;
  return h1g.log10().toNumber() >= log10Threshold;
}

// Compares the displayed pre-upgrade per-cell effective Hov gain values for
// H1G (cell 0) and H9G (cell 8). Requires a strictly positive H1G so a
// pre-game zero state cannot trivially satisfy the ratio.
function isRankGapRatioMet(snapshot: HoverIdleSnapshot): boolean {
  const h1g =
    snapshot.hovTargetCells[FIRST_HOV_TARGET_CELL_INDEX]
      ?.effectiveHovGainPerSecond;
  const h9g =
    snapshot.hovTargetCells[NINTH_HOV_TARGET_CELL_INDEX]
      ?.effectiveHovGainPerSecond;
  if (!h1g || !h9g) return false;
  if (compareHuge(h1g, 0) <= 0) return false;
  return compareHuge(h9g, h1g.mul(RANK_GAP_H9G_TO_H1G_RATIO_THRESHOLD)) >= 0;
}

function isNHovKnowsNHovUnlocked(snapshot: HoverIdleSnapshot): boolean {
  const effects = resolveNHovBranchEffects({
    branches: snapshot.nHov.derivedBranchLevels,
  });
  return (
    compareHuge(
      effects.hgNHovEffectsToHngTransferMultiplier,
      NHOV_KNOWS_NHOV_HG_EFFECTS_TO_HNG_THRESHOLD,
    ) >= 0
  );
}

function isRedAmplifierFullChargeUnlocked(
  snapshot: HoverIdleSnapshot,
): boolean {
  return snapshot.cumulativeStats.redAmplifierFullChargeReached;
}

export const FAST_OUTSIDE_HOV_MAX_DURATION_MS = 60 * 60 * 1000;
export const QUICK_OUTSIDE_HOV_MAX_DURATION_MS = 10 * 60 * 1000;
export const SWIFT_OUTSIDE_HOV_MAX_DURATION_MS = 60 * 1000;
export const TENTH_OUTSIDE_HOV_COUNT_THRESHOLD = 10;
export const MHOV_MP_EXECUTION_TEN_COUNT_THRESHOLD = 10;

function isSkipHovGainUnlocked(snapshot: HoverIdleSnapshot): boolean {
  if (snapshot.hovTargetCells[NINTH_HOV_TARGET_CELL_INDEX]?.unlocked !== true) {
    return false;
  }

  for (
    let index = FIRST_OPTIONAL_HOV_TARGET_CELL_INDEX;
    index < NINTH_HOV_TARGET_CELL_INDEX;
    index += 1
  ) {
    if (snapshot.hovTargetCells[index]?.unlocked === true) {
      return false;
    }
  }

  return true;
}

function isCheapFourthHovGainUnlocked(snapshot: HoverIdleSnapshot): boolean {
  const recordedCost =
    snapshot.minHovGainUnlockCostsByTargetIndex[FOURTH_HOV_TARGET_CELL_INDEX];
  return (
    recordedCost !== null &&
    recordedCost !== undefined &&
    compareHuge(recordedCost, CHEAP_FOURTH_HOV_GAIN_UNLOCK_COST_THRESHOLD) <= 0
  );
}

// Judged from the cost actually paid when an HG milestone was triggered, not a
// recomputed current cost, so this achievement's own discount cannot feed back
// into its unlock condition.
function isCheapGlobalHovGainMilestoneUnlocked(
  snapshot: HoverIdleSnapshot,
): boolean {
  return snapshot.minHovGainMilestoneTriggerCostsByMilestoneIndex.some(
    (recordedCost) =>
      recordedCost !== null &&
      recordedCost !== undefined &&
      compareHuge(
        recordedCost,
        CHEAP_GLOBAL_HOV_GAIN_MILESTONE_TRIGGER_COST_THRESHOLD,
      ) <= 0,
  );
}

// Index into minHovGainMilestoneTriggerCostsByMilestoneIndex for the HG
// milestone that crosses ONE_COST_BATCH_UPGRADE_HOV_GAIN_LEVEL. Milestones are
// recorded at every UPGRADE_MILESTONE_INTERVAL purchases, so the milestone for
// level N has index N / interval - 1.
const ONE_COST_BATCH_UPGRADE_HOV_GAIN_MILESTONE_INDEX =
  ONE_COST_BATCH_UPGRADE_HOV_GAIN_LEVEL / UPGRADE_MILESTONE_INTERVAL - 1;

// Judged from the cost actually paid at the level-1000 HG milestone purchase,
// so the achievement's own batching effect cannot feed back into its unlock
// condition.
function isOneCostBatchUpgradeUnlocked(snapshot: HoverIdleSnapshot): boolean {
  const recordedCost =
    snapshot.minHovGainMilestoneTriggerCostsByMilestoneIndex[
      ONE_COST_BATCH_UPGRADE_HOV_GAIN_MILESTONE_INDEX
    ];
  return (
    recordedCost !== null &&
    recordedCost !== undefined &&
    compareHuge(recordedCost, ONE_COST_BATCH_UPGRADE_TRIGGER_COST_THRESHOLD) <=
      0
  );
}

export const normalAchievementDefinitions: readonly NormalAchievementDefinition[] =
  [
    {
      id: "thousand",
      category: "normal",
      isUnlocked: (snapshot) => compareHuge(snapshot.hov, 1000) >= 0,
    },
    {
      id: "first-merge-hov",
      category: "normal",
      isUnlocked: (snapshot) => 1 <= snapshot.mHov.count,
    },
    {
      id: "hov-gain-family-upgrade",
      category: "normal",
      isUnlocked: (snapshot) =>
        snapshot.cumulativeStats.hovGainFamilyUpgradesPurchased >=
        HOV_GAIN_FAMILY_UPGRADE_PURCHASE_THRESHOLD,
    },
    {
      id: "fourth-hov-gain",
      category: "normal",
      isUnlocked: (snapshot) =>
        snapshot.hovTargetCells[FOURTH_HOV_TARGET_CELL_INDEX]?.unlocked ===
        true,
    },
    {
      id: "second-merge-hov",
      category: "normal",
      isUnlocked: (snapshot) => 2 <= snapshot.mHov.count,
    },
    {
      id: "pop",
      category: "normal",
      isUnlocked: (snapshot) =>
        compareHuge(
          huge(snapshot.mHov.hgMultiplier).mul(
            POP_GRID_INCREASE_TARGET_SECONDS,
          ),
          HOV_GRID_INCREASE_1_UNLOCK_CURRENT_LOOP_HOV_THRESHOLD,
        ) >= 0,
    },
    {
      id: "global-hov-gain-upgrade-milestone",
      category: "normal",
      isUnlocked: (snapshot) =>
        snapshot.hovUpgradeEnhancementLevel >=
        GLOBAL_HOV_GAIN_UPGRADE_MILESTONE_ACHIEVEMENT_LEVEL,
    },
    {
      id: "billion",
      category: "normal",
      isUnlocked: (snapshot) =>
        compareHuge(snapshot.hov, BILLION_HOV_THRESHOLD) >= 0,
    },
    {
      id: "bomb",
      category: "normal",
      isUnlocked: (snapshot) =>
        compareHuge(
          huge(snapshot.mHov.hgMultiplier).mul(
            BOMB_GRID_INCREASE_TARGET_SECONDS,
          ),
          HOV_GRID_INCREASE_2_UNLOCK_CURRENT_LOOP_HOV_THRESHOLD,
        ) >= 0,
    },
    {
      id: "tim-hov-activation",
      category: "normal",
      isUnlocked: (snapshot) =>
        compareHuge(
          snapshot.cumulativeStats.lifetimeHovRefinedFromTim,
          TIM_HOV_ACTIVATION_UNLOCK_HOV_REFINED_FROM_TIM_THRESHOLD,
        ) >= 0,
    },
    {
      id: "time-has-come",
      category: "normal",
      isUnlocked: (snapshot) =>
        compareHuge(
          snapshot.cumulativeStats.totalTimSpent,
          TIME_HAS_COME_TOTAL_TIM_SPENT_THRESHOLD,
        ) >= 0,
    },
    {
      id: "mass-hov-gain-family-upgrade",
      category: "normal",
      isUnlocked: (snapshot) =>
        snapshot.cumulativeStats.hovGainFamilyUpgradesPurchased >=
        MASS_HOV_GAIN_FAMILY_UPGRADE_PURCHASE_THRESHOLD,
    },
    {
      id: "first-new-hov",
      category: "normal",
      isUnlocked: (snapshot) => snapshot.nHov.count >= 1,
    },
    {
      id: "ninth-hov-gain",
      category: "normal",
      isUnlocked: (snapshot) =>
        snapshot.hovTargetCells[NINTH_HOV_TARGET_CELL_INDEX]?.unlocked === true,
    },
    {
      id: "music-pointer",
      category: "normal",
      isUnlocked: (snapshot) =>
        computeMpmAssignedMpOccupancy(snapshot.mpm) >=
        MUSIC_POINTER_ASSIGNED_MP_THRESHOLD,
    },
    {
      id: "movie-pointer",
      category: "normal",
      isUnlocked: (snapshot) =>
        computeMpmAssignedMpOccupancy(snapshot.mpm) >=
        MOVIE_POINTER_ASSIGNED_MP_THRESHOLD,
    },
    {
      id: "maschinen-pointer",
      category: "normal",
      isUnlocked: (snapshot) =>
        computeMpmAssignedMpOccupancy(snapshot.mpm) >=
        MASCHINEN_POINTER_ASSIGNED_MP_THRESHOLD,
    },
    {
      id: "multi-branch-new-hov",
      category: "normal",
      isUnlocked: (snapshot) =>
        Object.values(snapshot.nHov.derivedBranchLevels).filter(
          (level) => level > 0,
        ).length >= NEW_NEW_HOV_EXECUTED_BRANCH_KINDS_THRESHOLD,
    },
    {
      id: "cheap-fourth-hov-gain",
      category: "normal",
      isUnlocked: isCheapFourthHovGainUnlocked,
    },
    {
      id: "cheap-global-hov-gain-milestone",
      category: "normal",
      isUnlocked: isCheapGlobalHovGainMilestoneUnlocked,
    },
    {
      id: "skip-hov-gain",
      category: "normal",
      isUnlocked: isSkipHovGainUnlocked,
    },
    {
      id: "merge-hov-only-one-hov-target-cell",
      category: "normal",
      // Judged from the record taken at MHov execution time, not from the
      // current cell state, because MHov resets unlocked Hov cells right after.
      isUnlocked: (snapshot) =>
        snapshot.cumulativeStats.hasExecutedMergeHovWithOnlyOneHovTargetCell ===
        true,
    },
    {
      id: "hov-9-gain-upgrade-milestone",
      category: "normal",
      isUnlocked: (snapshot) =>
        (snapshot.hovTargetCells[NINTH_HOV_TARGET_CELL_INDEX]
          ?.enhancementLevel ?? 0) >= H9G_UPGRADE_MILESTONE_ACHIEVEMENT_LEVEL,
    },
    {
      id: "vigintillion",
      category: "normal",
      isUnlocked: (snapshot) =>
        compareHuge(snapshot.hov, VIGINTILLION_HOV_THRESHOLD) >= 0,
    },
    {
      id: "one-cost-batch-upgrade",
      category: "normal",
      isUnlocked: isOneCostBatchUpgradeUnlocked,
    },
    {
      id: "hov-1-gain-tries-too",
      category: "normal",
      isUnlocked: (snapshot) =>
        isH1gValueLog10AtLeast(snapshot, HOV_1_GAIN_TRIES_TOO_LOG10_THRESHOLD),
    },
    {
      id: "google",
      category: "normal",
      isUnlocked: (snapshot) =>
        compareHuge(snapshot.hov, GOOGLE_HOV_THRESHOLD) >= 0,
    },
    {
      id: "hov-gain-family-upgrade-marathon",
      category: "normal",
      isUnlocked: (snapshot) =>
        snapshot.cumulativeStats.hovGainFamilyUpgradesPurchased >=
        HOV_GAIN_FAMILY_UPGRADE_MARATHON_PURCHASE_THRESHOLD,
    },
    {
      id: "many-new-hov",
      category: "normal",
      isUnlocked: (snapshot) =>
        snapshot.nHov.count >= MANY_NEW_HOV_NHOV_COUNT_THRESHOLD,
    },
    {
      id: "global-hov-gain-upgrade-tenth-milestone",
      category: "normal",
      isUnlocked: (snapshot) =>
        snapshot.hovUpgradeEnhancementLevel >=
        GLOBAL_HOV_GAIN_UPGRADE_TENTH_MILESTONE_ACHIEVEMENT_LEVEL,
    },
    {
      id: "not-fully-free",
      category: "normal",
      // Judged from the cumulative record taken at NHov execution time, not
      // from the current branch level, so the unlock is gated on a real
      // execution that crossed the threshold rather than passive snapshot
      // state.
      isUnlocked: (snapshot) =>
        snapshot.cumulativeStats
          .hasExecutedNHovExceedingNotFullyFreeThreshold === true,
    },
    {
      id: "nhov-knows-nhov",
      category: "normal",
      isUnlocked: isNHovKnowsNHovUnlocked,
    },
    {
      id: "first-outside-hov",
      category: "normal",
      isUnlocked: (snapshot) => snapshot.oHov.count >= 1,
    },
    {
      id: "red-amplifier-full-charge",
      category: "normal",
      isUnlocked: isRedAmplifierFullChargeUnlocked,
    },
    {
      id: "second-outside-hov",
      category: "normal",
      isUnlocked: (snapshot) => snapshot.oHov.count >= 2,
    },
    {
      id: "fast-outside-hov",
      category: "normal",
      // Game-time based so admin/system speed multipliers cannot trivialize
      // the unlock; the threshold is "1 in-game hour".
      isUnlocked: (snapshot) => {
        const fastest = snapshot.oHov.fastestRunGameDurationMs;
        return fastest !== null && fastest <= FAST_OUTSIDE_HOV_MAX_DURATION_MS;
      },
    },
    {
      id: "thousand-ap",
      category: "normal",
      isUnlocked: (snapshot) =>
        compareHuge(snapshot.hovAmplifier.ap, THOUSAND_AP_THRESHOLD) >= 0,
    },
    {
      id: "tenth-outside-hov",
      category: "normal",
      isUnlocked: (snapshot) =>
        snapshot.oHov.count >= TENTH_OUTSIDE_HOV_COUNT_THRESHOLD,
    },
    {
      id: "mhov-mp-execution-ten",
      category: "normal",
      // Counts only MP-driven MHov executions tracked at the authoritative
      // `maybeAutoExecuteMHovMpExecution` call site, so manual MHov never
      // contributes.
      isUnlocked: (snapshot) =>
        snapshot.cumulativeStats.mhovExecutedByMpCount >=
        MHOV_MP_EXECUTION_TEN_COUNT_THRESHOLD,
    },
    {
      id: "massive-merge-hov",
      category: "normal",
      // Judged from the cumulative record taken at MHov execution time, not
      // from the current `mHov.hgMultiplier`, so the unlock is gated on a
      // single MHov execution whose before/after multiplier ratio crossed the
      // threshold rather than passive snapshot state. Kept distinct from
      // `pop` / `bomb`, which judge the Hov/HG scale at MHov execution time.
      isUnlocked: (snapshot) =>
        snapshot.cumulativeStats.hasExecutedMassiveMergeHov === true,
    },
    {
      id: "burn-the-core",
      category: "normal",
      isUnlocked: (snapshot) =>
        compareHuge(
          resolveTotalHovCoreSpent(snapshot.cumulativeStats),
          BURN_THE_CORE_TOTAL_HOV_CORE_SPENT_THRESHOLD,
        ) >= 0,
    },
    {
      id: "mp-wall-preset",
      category: "normal",
      isUnlocked: (snapshot) =>
        snapshot.wall.walls.some(
          (wall) =>
            wall.id === "mp-wall" && wall.firstClearRewardClaimed === true,
        ),
    },
    {
      id: "quick-outside-hov",
      category: "normal",
      // Mirrors fast-outside-hov: judged against game-time, not wall time.
      isUnlocked: (snapshot) => {
        const fastest = snapshot.oHov.fastestRunGameDurationMs;
        return fastest !== null && fastest <= QUICK_OUTSIDE_HOV_MAX_DURATION_MS;
      },
    },
    {
      id: "outside-mhov",
      category: "normal",
      // Latched at MHov execution time when the raw reward HG multiplier
      // reaches the Number-limit boundary, then preserved across resets. The
      // achievement lifts the hard cap while the post-boundary softcap remains.
      isUnlocked: (snapshot) =>
        snapshot.cumulativeStats.hasExecutedOutsideMhov === true,
    },
    {
      id: "outside-nhov",
      category: "normal",
      // Latched at NHov execution time when the /n HG cost branch reaches the
      // Number-limit boundary, then preserved across resets. The achievement
      // effect lifts the 1.8e308 cap on all NHov branch effects.
      isUnlocked: (snapshot) =>
        snapshot.cumulativeStats.hasExecutedOutsideNhov === true,
    },
    {
      id: "rank-gap",
      category: "normal",
      isUnlocked: isRankGapRatioMet,
    },
    {
      id: "new-hov-without-merge-hov",
      category: "normal",
      // Judged from the record taken at NHov execution time, not from the
      // current mHov/nHov counts, because NHov resets mHov state right after.
      isUnlocked: (snapshot) =>
        snapshot.cumulativeStats.hasExecutedNewHovWithoutMergeHov === true,
    },
    {
      id: "swift-outside-hov",
      category: "normal",
      // Mirrors fast-outside-hov: judged against game-time, not wall time.
      isUnlocked: (snapshot) => {
        const fastest = snapshot.oHov.fastestRunGameDurationMs;
        return fastest !== null && fastest <= SWIFT_OUTSIDE_HOV_MAX_DURATION_MS;
      },
    },
    {
      id: "amplifier-upgrade",
      category: "normal",
      isUnlocked: (snapshot) =>
        snapshot.cumulativeStats.amplifierUpgradesPurchased >=
        AMPLIFIER_UPGRADE_PURCHASE_THRESHOLD,
    },
    {
      id: "hov-1-gain-gets-serious",
      category: "normal",
      isUnlocked: (snapshot) =>
        isH1gValueLog10AtLeast(
          snapshot,
          HOV_1_GAIN_GETS_SERIOUS_LOG10_THRESHOLD,
        ),
    },
    // EA scope: HovTim balance slider achievement is deferred.
    // {
    //   id: "hov-hov",
    //   category: "normal",
    //   isUnlocked: (snapshot) => snapshot.hovTimBalance.hovAllocation > 0,
    // },
    {
      id: "exploration-tim-acceleration",
      category: "normal",
      isUnlocked: (snapshot) =>
        snapshot.exploration.cumulativeTimAddedMs >=
        EXPLORATION_TIM_ACCELERATION_UNLOCK_CUMULATIVE_MS,
    },
    {
      id: "exploration-biome-level-two",
      category: "normal",
      isUnlocked: (snapshot) =>
        snapshot.exploration.biomeLevel >=
        EXPLORATION_BIOME_LEVEL_ACHIEVEMENT_THRESHOLD,
    },
  ];
